import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, apiError, imgUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Trash2, FolderPlus, Send, Palette, PersonStanding, FileText, Wand2, Save, Sparkles } from "lucide-react";
import { TechpackWizard } from "../components/TechpackWizard";
import RemixModal from "../components/RemixModal";

function DescriptionBlock({ text }) {
  if (!text) return null;
  return (
    <div className="whitespace-pre-line text-sm text-zinc-700 leading-relaxed" data-testid="product-description-block">
      {text}
    </div>
  );
}
export { DescriptionBlock };

export default function DesignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [design, setDesign] = useState(null);
  const [activeVersion, setActiveVersion] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [users, setUsers] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [pinToVersion, setPinToVersion] = useState(true);
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState("");
  const [showTechpackWizard, setShowTechpackWizard] = useState(false);
  const [remixOpen, setRemixOpen] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState("");
  const [titleDraft, setTitleDraft] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [savingDescription, setSavingDescription] = useState(false);

  useEffect(() => {
    api.get(`/designs/${id}`).then((r) => {
      setDesign(r.data);
      setActiveVersion(r.data.versions[r.data.versions.length - 1]);
    }).catch(() => navigate("/designs"));
    api.get(`/designs/${id}/comments`).then((r) => setComments(r.data)).catch(() => {});
    api.get("/collections").then((r) => setCollections(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [id, navigate]);

  const onCommentChange = (v) => {
    setCommentText(v);
    const m = v.match(/@([\w]*)$/);
    setMentionQuery(m ? m[1] : null);
  };

  const pickMention = (name) => {
    setCommentText(commentText.replace(/@([\w]*)$/, `@${name} `));
    setMentionQuery(null);
  };

  const mentionMatches = mentionQuery !== null
    ? users.filter((u) => u.name.toLowerCase().startsWith(mentionQuery.toLowerCase())).slice(0, 5)
    : [];

  const highlightMentions = (c) => {
    const names = c.mentions || [];
    if (!names.length) return c.text;
    const re = new RegExp(`(${names.map((n) => "@" + n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "g");
    return c.text.split(re).map((p, i) =>
      p.startsWith("@") && names.includes(p.slice(1))
        ? <span key={i} className="bg-zinc-950 text-white px-1.5 py-0.5 rounded text-xs font-medium">{p}</span>
        : p
    );
  };

  const addComment = async () => {
    if (!commentText.trim()) return;
    try {
      const mentions = users.filter((u) => commentText.includes(`@${u.name}`)).map((u) => u.name);
      const { data } = await api.post(`/designs/${id}/comments`, {
        text: commentText,
        mentions,
        version_id: pinToVersion && activeVersion ? activeVersion.id : null,
      });
      setComments([...comments, data]);
      setCommentText("");
      setMentionQuery(null);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addToCollection = async () => {
    if (!selectedCollection) return;
    try {
      const { data } = await api.post(`/collections/${selectedCollection}/designs`, {
        design_id: id,
        version_id: activeVersion?.id || null,
      });
      if (data.duplicate) toast.info("This iteration is already in that collection");
      else toast.success(`Added ${versionLabel(activeVersion?.id) || "design"} to collection`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const versionLabel = (versionId) => {
    const i = design?.versions?.findIndex((v) => v.id === versionId);
    return i >= 0 ? `v${i + 1}` : null;
  };

  const jumpToVersion = (versionId) => {
    const v = design?.versions?.find((x) => x.id === versionId);
    if (v) setActiveVersion(v);
  };

  const deleteDesign = async () => {
    if (!window.confirm("Delete this design and all its iterations?")) return;
    try {
      await api.delete(`/designs/${id}`);
      toast.success("Design deleted");
      navigate("/designs");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!design) return <div className="p-10 text-sm text-zinc-400">Loading…</div>;

  const canDelete = user?.role === "admin" || user?.id === design.owner_id;

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <button data-testid="design-back-button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950 transition-colors">
        <ArrowLeft size={14} /> Back
      </button>
      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="design-title">{design.title}</h1>
          <p className="text-sm text-zinc-500 mt-1">By {design.owner_name} · {design.versions.length} iteration{design.versions.length > 1 ? "s" : ""} · {design.category}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/studio/materials" className="inline-flex items-center gap-1.5 text-sm border border-zinc-300 px-3.5 py-2 rounded-md hover:bg-zinc-100 transition-colors" data-testid="design-apply-material-link"><Palette size={14} /> Materials</Link>
          <Link to="/studio/tryon" className="inline-flex items-center gap-1.5 text-sm border border-zinc-300 px-3.5 py-2 rounded-md hover:bg-zinc-100 transition-colors" data-testid="design-tryon-link"><PersonStanding size={14} /> Try-on</Link>
          <button data-testid="design-create-techpack-button" onClick={() => setShowTechpackWizard(true)} className="inline-flex items-center gap-1.5 text-sm bg-zinc-950 text-white px-3.5 py-2 rounded-md hover:bg-zinc-800 transition-colors">
            <FileText size={14} /> Create techpack
          </button>
          {canDelete && (
            <button data-testid="design-delete-button" onClick={deleteDesign} className="p-2.5 border border-zinc-300 rounded-md hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
          )}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {activeVersion && (
            <img data-testid="design-active-image" src={imgUrl(activeVersion.image)} alt={design.title}
              className="w-full max-h-[65vh] object-contain rounded-lg border border-zinc-200 bg-white" />
          )}
          <div className="mt-4">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2">Iteration history</p>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {design.versions.map((v, i) => (
                <button key={v.id} data-testid={`design-version-${i}`} onClick={() => setActiveVersion(v)}
                  className={`shrink-0 border rounded-md overflow-hidden w-24 transition-colors ${activeVersion?.id === v.id ? "border-zinc-950 ring-2 ring-zinc-950" : "border-zinc-200 hover:border-zinc-400"}`}>
                  <img src={imgUrl(v.image)} alt={`v${i + 1}`} className="w-full h-24 object-cover" />
                  <p className="text-[10px] p-1 text-center text-zinc-500">v{i + 1}</p>
                </button>
              ))}
            </div>
            {activeVersion && (
              <div className="mt-1 flex items-center justify-between gap-2 flex-wrap">
                <p className="text-xs text-zinc-500">{activeVersion.tool.replace(/_/g, " ")} — {activeVersion.prompt || "no notes"} · by {activeVersion.created_by}</p>
                {activeVersion.tool === "model_tryon" && (
                  <button data-testid="design-remix-button" onClick={() => setRemixOpen(true)}
                    className="inline-flex items-center gap-1.5 text-xs border border-zinc-300 rounded-md px-3 py-1 hover:bg-zinc-100 shrink-0">
                    <Sparkles size={12} /> Remix
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 border border-zinc-200 bg-white rounded-lg p-5" data-testid="design-description-block">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-medium">Marketing copy</p>
              {canDelete && !editingDescription && (
                <button data-testid="design-description-edit" onClick={() => { setDescriptionDraft(design.product_description || ""); setTitleDraft(design.product_title || ""); setEditingDescription(true); }}
                  className="text-xs text-zinc-500 hover:text-zinc-950">Edit</button>
              )}
            </div>
            {editingDescription ? (
              <>
                <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">Product card title <span className="text-zinc-300 normal-case">(5–6 words)</span></label>
                <input data-testid="design-title-input" value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)}
                  placeholder="Belted Double-Breasted Trench Coat"
                  className="mt-1 w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                <label className="mt-4 block text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">Long description</label>
                <textarea data-testid="design-description-textarea" rows={8} value={descriptionDraft} onChange={(e) => setDescriptionDraft(e.target.value)}
                  placeholder={"These very elegant pleated trousers!\n\n- Wide-leg, high-waisted trousers\n- Darts at the waist\n- Hook and zip fastening\n- Belt loops\n- 2 welt pockets at the sides\n- Inside leg length: approximately 78 cm\n- Leg opening: approximately 38 cm"}
                  className="mt-1 w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                  <button data-testid="design-description-ai" disabled={aiLoading} onClick={async () => {
                    setAiLoading(true);
                    try {
                      const { data } = await api.post(`/designs/${id}/generate-description`);
                      setDesign(data);
                      setDescriptionDraft(data.product_description || "");
                      setTitleDraft(data.product_title || "");
                      toast.success("AI copy drafted");
                    } catch (e) { toast.error(apiError(e)); }
                    finally { setAiLoading(false); }
                  }} className="inline-flex items-center gap-1.5 text-xs border border-zinc-300 rounded-md px-3 py-1.5 hover:bg-zinc-100 disabled:opacity-40">
                    <Wand2 size={12} /> {aiLoading ? "Drafting…" : "AI draft"}
                  </button>
                  <div className="flex gap-2">
                    <button onClick={() => setEditingDescription(false)} className="text-xs px-3 py-1.5 rounded-md hover:bg-zinc-100">Cancel</button>
                    <button data-testid="design-description-save" disabled={savingDescription} onClick={async () => {
                      setSavingDescription(true);
                      try {
                        const { data } = await api.patch(`/designs/${id}`, { product_title: titleDraft, product_description: descriptionDraft });
                        setDesign(data); setEditingDescription(false);
                        toast.success("Copy saved");
                      } catch (e) { toast.error(apiError(e)); }
                      finally { setSavingDescription(false); }
                    }} className="inline-flex items-center gap-1.5 bg-zinc-950 text-white text-xs px-3 py-1.5 rounded-md hover:bg-zinc-800 disabled:opacity-40">
                      <Save size={12} /> {savingDescription ? "Saving…" : "Save"}
                    </button>
                  </div>
                </div>
              </>
            ) : (design.product_title || design.product_description) ? (
              <>
                {design.product_title && <p className="text-lg font-heading font-bold" data-testid="design-product-title">{design.product_title}</p>}
                {design.product_description && <div className="mt-2"><DescriptionBlock text={design.product_description} /></div>}
              </>
            ) : (
              <p className="text-sm text-zinc-400 italic">No marketing copy yet. {canDelete && "Click Edit to write one or use AI draft."}</p>
            )}
          </div>

          <div className="mt-6 border border-zinc-200 bg-white rounded-lg p-5">
            <div className="flex items-center gap-3">
              <FolderPlus size={16} className="text-zinc-400 shrink-0" />
              <select data-testid="design-collection-select" value={selectedCollection} onChange={(e) => setSelectedCollection(e.target.value)}
                className="flex-1 border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="">Choose a collection…</option>
                {collections.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <button data-testid="design-add-to-collection-button" onClick={addToCollection} disabled={!selectedCollection}
                className="bg-zinc-950 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">Add</button>
            </div>
            <p className="text-[11px] text-zinc-400 mt-2 ml-7" data-testid="design-add-to-collection-hint">
              The iteration you're viewing ({versionLabel(activeVersion?.id) || "current"}) will be saved to the collection.
            </p>
          </div>
        </div>

        <div className="border border-zinc-200 bg-white rounded-lg p-5 h-fit">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-4">Team comments</p>
          <div className="space-y-4 max-h-96 overflow-y-auto pr-1" data-testid="design-comments-list">
            {comments.length === 0 && <p className="text-sm text-zinc-400">No comments yet — start the conversation.</p>}
            {comments.map((c) => (
              <div key={c.id}>
                <p className="text-xs font-medium flex items-center gap-1.5">
                  {c.user_name}
                  {c.version_id && versionLabel(c.version_id) && (
                    <button data-testid={`comment-version-badge-${c.id}`} onClick={() => jumpToVersion(c.version_id)}
                      className="border border-zinc-300 text-zinc-600 px-1.5 py-0.5 rounded text-[10px] font-medium hover:bg-zinc-950 hover:text-white hover:border-zinc-950 transition-colors">
                      {versionLabel(c.version_id)}
                    </button>
                  )}
                  <span className="text-zinc-400 font-normal">· {new Date(c.created_at).toLocaleDateString()}</span>
                </p>
                <p className="text-sm text-zinc-600 mt-0.5">{highlightMentions(c)}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 relative">
            <button data-testid="comment-pin-version-toggle" onClick={() => setPinToVersion(!pinToVersion)}
              className={`mb-2 text-[11px] px-2.5 py-1 rounded-full border transition-colors ${pinToVersion ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 text-zinc-500 hover:bg-zinc-100"}`}>
              {pinToVersion && activeVersion ? `Pinned to ${versionLabel(activeVersion.id) || "version"}` : "General comment"}
            </button>
            {mentionMatches.length > 0 && (
              <div className="absolute bottom-full mb-1 left-0 w-full bg-white border border-zinc-200 rounded-md shadow-md z-10" data-testid="mention-dropdown">
                {mentionMatches.map((u) => (
                  <button key={u.id} data-testid={`mention-option-${u.id}`} onClick={() => pickMention(u.name)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-100 transition-colors">
                    <span className="font-medium">@{u.name}</span> <span className="text-xs text-zinc-400">{u.email}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input data-testid="design-comment-input" value={commentText} onChange={(e) => onCommentChange(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && mentionMatches.length === 0 && addComment()} placeholder="Add a comment… use @ to mention"
                className="flex-1 border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <button data-testid="design-comment-submit" onClick={addComment} className="bg-zinc-950 text-white p-2.5 rounded-md hover:bg-zinc-800 transition-colors"><Send size={14} /></button>
            </div>
          </div>
        </div>
      </div>
      <TechpackWizard
        open={showTechpackWizard}
        mode="single"
        design={{ id, version_id: activeVersion?.id }}
        onClose={() => setShowTechpackWizard(false)}
        onCreated={(tp) => { setShowTechpackWizard(false); navigate(`/techpacks/${tp.id}`); }}
      />
      <RemixModal
        open={remixOpen}
        designId={id}
        sourceVersionId={activeVersion?.id}
        onClose={() => setRemixOpen(false)}
        onRemixed={(next) => {
          setDesign(next);
          setActiveVersion(next.versions[next.versions.length - 1]);
        }}
      />
    </div>
  );
}
